import React, { useState, useRef } from 'react'
import { Checkbox, Col, Input, Radio, Row } from 'antd'
import styled from 'styled-components'

const Label = styled.div`
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
`
const ErrorText = styled.div`
  color: red;
  font-size: 12px;
  margin-top: 4px;
`

const StyledRadioGroup = styled(Radio.Group)`
  .ant-radio-wrapper {
    background-color: white;
    padding: 4px 8px;
    border-radius: 4px;
    margin-right: 8px;
  }
  .ant-radio-inner {
    background-color: white;
  }
`

// Total months for a {years, months} pair — used for strict validation/compare.
const toMonthsTotal = (years, months) =>
  (Number(years) || 0) * 12 + (Number(months) || 0)

const AgeFilter = ({
  filterValues,
  setFilterValues,
  selectedQuickOption,
  setSelectedQuickOption,
}) => {
  const [error, setError] = useState('')
  const errorTimeout = useRef(null)

  const isStrict = !!filterValues.selectedAge?.strict

  // validate with delay
  const validate = age => {
    if (errorTimeout.current) clearTimeout(errorTimeout.current)

    errorTimeout.current = setTimeout(() => {
      const { from, to, fromMonths, toMonths } = age || {}
      // When strict, compare on total months; otherwise compare whole years.
      const fromVal = isStrict ? toMonthsTotal(from, fromMonths) : Number(from)
      const toVal = isStrict ? toMonthsTotal(to, toMonths) : Number(to)
      if ((from || to) && from && to && toVal < fromVal) {
        setError(
          isStrict
            ? '"To" age (years & months) must be greater than or equal to "From"'
            : '"To" age must be greater than or equal to "From" age'
        )
      } else {
        setError('')
      }
    }, 500) // delay 500ms
  }

  const handleQuickSelectChange = e => {
    const value = e.target.value
    setSelectedQuickOption(value)

    let from = null,
      to = null

    switch (value) {
      case 'lte22':
        from = 0
        to = 22
        break
      case '21to22':
        from = 21
        to = 22
        break
      case '22to25':
        from = 22
        to = 25
        break
      case 'gte25':
        from = 25
        to = 100
        break
      default:
        break
    }

    setFilterValues(prev => ({
      ...prev,
      // Quick presets are whole years — clear any manual months.
      selectedAge: {
        ...prev.selectedAge,
        from: null,
        to: null,
        fromMonths: null,
        toMonths: null,
      },
      selectedQuickAge: { from, to },
    }))
    setError('')
  }

  const handleStrictChange = e => {
    const strict = e.target.checked
    setFilterValues(prev => ({
      ...prev,
      selectedAge: {
        ...prev.selectedAge,
        strict,
        // Unchecking strict drops month precision.
        ...(strict ? {} : { fromMonths: null, toMonths: null }),
      },
    }))
    setError('')
  }

  const handleInputChange = (value, type) => {
    setSelectedQuickOption(null)

    const digitsOnly = (value ?? '').toString().replace(/\D/g, '')
    let numeric = digitsOnly === '' ? null : Number(digitsOnly)
    // Months are 0–11.
    if ((type === 'fromMonths' || type === 'toMonths') && numeric !== null) {
      numeric = Math.min(numeric, 11)
    }

    setFilterValues(prev => {
      const updated = {
        ...prev,
        selectedQuickAge: { from: null, to: null },
        selectedAge: {
          ...prev.selectedAge,
          [type]: numeric,
        },
      }

      validate(updated.selectedAge)
      return updated
    })
  }

  return (
    <>
      <Row style={{ rowGap: '0px !important' }}>
        <Col span={12} style={{ padding: '8px' }}>
          <Label>From</Label>
          <Row gutter={8}>
            <Col span={isStrict ? 12 : 24}>
              <Input
                width="100%"
                type="text"
                name="fromAge"
                value={filterValues.selectedAge.from}
                placeholder={isStrict ? 'Years' : 'Enter age (From)'}
                onChange={e => handleInputChange(e.target.value, 'from')}
                maxLength={3}
                inputMode="numeric"
                pattern="[0-9]*"
              />
            </Col>
            {isStrict && (
              <Col span={12}>
                <Input
                  width="100%"
                  type="text"
                  name="fromAgeMonths"
                  value={filterValues.selectedAge.fromMonths}
                  placeholder="Months (0-11)"
                  onChange={e => handleInputChange(e.target.value, 'fromMonths')}
                  maxLength={2}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </Col>
            )}
          </Row>
        </Col>
        <Col span={12} style={{ padding: '8px' }}>
          <Label>To</Label>
          <Row gutter={8}>
            <Col span={isStrict ? 12 : 24}>
              <Input
                width="100%"
                type="text"
                name="toAge"
                value={filterValues.selectedAge.to}
                placeholder={isStrict ? 'Years' : 'Enter age (To)'}
                onChange={e => handleInputChange(e.target.value, 'to')}
                maxLength={3}
                inputMode="numeric"
                pattern="[0-9]*"
              />
            </Col>
            {isStrict && (
              <Col span={12}>
                <Input
                  width="100%"
                  type="text"
                  name="toAgeMonths"
                  value={filterValues.selectedAge.toMonths}
                  placeholder="Months (0-11)"
                  onChange={e => handleInputChange(e.target.value, 'toMonths')}
                  maxLength={2}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </Col>
            )}
          </Row>
        </Col>
      </Row>

      <Row style={{ marginTop: '4px' }}>
        <Col span={24} style={{ padding: '0 8px' }}>
          <Checkbox checked={isStrict} onChange={handleStrictChange}>
            Strict (consider months)
          </Checkbox>
        </Col>
      </Row>

      {error && (
        <Row>
          <Col span={24}>
            <ErrorText>{error}</ErrorText>
          </Col>
        </Row>
      )}

      <Row style={{ marginTop: '10px', rowGap: '0px !important' }}>
        <Label style={{ fontWeight: 'bold', color: '#000000' }}>
          Quick Selection
        </Label>
      </Row>

      <Row style={{ marginTop: '10px' }}>
        <Col span={24}>
          <StyledRadioGroup
            onChange={handleQuickSelectChange}
            value={selectedQuickOption}
          >
            <Radio value="lte22">≤ 22</Radio>
            <Radio value="21to22">21 - 22</Radio>
            <Radio value="22to25">22 - 25</Radio>
            <Radio value="gte25">≥25</Radio>
          </StyledRadioGroup>
        </Col>
      </Row>
    </>
  )
}

export default AgeFilter
